/**
 * 新霓 ウィジェット計測（GA4）
 *
 * 「送料無料まであと少し」（商品ページ・カートページ・ドロワー）と Pickup Products について、
 * 表示・クリック・カート追加を GA4 に送ります。
 * 既存のウィジェットのコードには手を入れず、ページ全体を監視して動きます。
 *
 * 設置：assets/widget-analytics.js として保存し、layout/theme.liquid の </body> の直前に
 *   <script src="{{ 'widget-analytics.js' | asset_url }}" defer></script>
 * を追加します。
 *
 * 送る内容
 *   view_item_list          ウィジェットの商品が画面に表示された（GA4 標準イベント）
 *   select_item             ウィジェットの商品リンクが押された（GA4 標準イベント）
 *   widget_quick_add_click  ウィジェットのクイック追加ボタンが押された
 *   widget_add_to_cart      ウィジェットで触った商品が、その後カートに入った
 *   pickup_popup_toggle     Pickup Products を開いた／閉じた
 */
(() => {
  if (window.__sanngaiWidgetAnalytics) return;
  window.__sanngaiWidgetAnalytics = true;

  // 送料無料のしきい値（税込・円）。変更したら、テーマ設定とあわせてここも直してください
  const FREE_SHIPPING_THRESHOLD = Number(window.SANNGAI_FREE_SHIPPING_THRESHOLD) || 9000;
  // ウィジェットで触ってからこの時間内に同じ商品がカートに入れば、ウィジェット経由とみなす
  const ATTRIBUTION_MS = 30 * 60 * 1000;
  const STORE_KEY = 'sanngai:widget-touches';

  const WIDGETS = [
    {
      item: '[data-gap-item]',
      list: el => el.closest('cart-drawer') ? 'shipping_gap_drawer'
        : location.pathname.indexOf('/cart') === 0 ? 'shipping_gap_cart'
        : 'shipping_gap_product',
      name: '送料無料まであと少し',
      title: el => text(el.querySelector('a.link-underline') || el.querySelector('a[href*="/products/"]')),
    },
    {
      item: 'pickup-products-popup [data-popup-grid] > li',
      list: () => 'pickup_popup',
      name: 'Pickup Products',
      title: el => (el.querySelector('a[aria-label]') || {}).getAttribute?.('aria-label') || text(el),
    },
  ];

  const send = (name, params) => {
    if (typeof window.gtag === 'function') window.gtag('event', name, params);
  };
  const text = el => (el && el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  const handleOf = el => {
    const a = el.querySelector('a[href*="/products/"]');
    const m = a && /\/products\/([^/?#]+)/.exec(a.getAttribute('href'));
    return m ? decodeURIComponent(m[1]) : '';
  };
  const widgetOf = el => WIDGETS.find(w => el.matches(w.item));
  const indexIn = el => Array.prototype.indexOf.call(el.parentElement.children, el);
  const itemFor = (w, el) => ({
    item_id: handleOf(el),
    item_name: w.title(el),
    item_list_id: w.list(el),
    item_list_name: w.name,
    index: indexIn(el),
  });

  // ウィジェットで触った商品。ページをまたいで残すため sessionStorage に置く
  const loadTouches = () => {
    try {
      const all = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
      const now = Date.now();
      Object.keys(all).forEach(h => { if (now - all[h].at > ATTRIBUTION_MS) delete all[h]; });
      return all;
    } catch (e) { return {}; }
  };
  const saveTouches = all => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(all)); } catch (e) { /* 保存できなくても計測以外に影響しない */ }
  };

  // ---------------------------------------------------------------
  // カートの金額（円）
  // ---------------------------------------------------------------
  const nativeFetch = window.fetch.bind(window);
  let cartValue = null;
  const refreshCart = () => nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(r => r.json())
    .then(c => { cartValue = Math.round(c.total_price / 100); return cartValue; })
    .catch(() => cartValue);

  // ---------------------------------------------------------------
  // 表示：画面に半分以上入った商品を、ページ表示ごとに1回だけ送る
  // ---------------------------------------------------------------
  const seen = new Set();
  const pending = new Map(); // list_id -> [{w, el}]
  let flushTimer = null;

  const flush = () => {
    flushTimer = null;
    pending.forEach((entries, listId) => {
      const items = entries.map(({ w, el }) => itemFor(w, el));
      refreshCart().then(value => send('view_item_list', {
        item_list_id: listId,
        item_list_name: entries[0].w.name,
        items,
        cart_value: value,
        shipping_gap: value === null ? undefined : Math.max(FREE_SHIPPING_THRESHOLD - value, 0),
      }));
    });
    pending.clear();
  };

  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
      const el = entry.target;
      const w = widgetOf(el);
      if (!w || el.hidden || el.closest('[hidden]')) return;
      const listId = w.list(el);
      const key = listId + '|' + handleOf(el);
      if (seen.has(key)) return;
      seen.add(key);
      if (!pending.has(listId)) pending.set(listId, []);
      pending.get(listId).push({ w, el });
      if (!flushTimer) flushTimer = setTimeout(flush, 800);
    });
  }, { threshold: [0, 0.5] });

  // ウィジェットは hidden 属性で表示を切り替えるため、その変化と、描き直しで追加された要素を観察し直す
  // （スライダーが class を頻繁に書き換えるので、class と style は監視しない）
  const watch = root => WIDGETS.forEach(w => {
    root.querySelectorAll?.(w.item).forEach(el => { io.unobserve(el); io.observe(el); });
    if (root.matches?.(w.item)) { io.unobserve(root); io.observe(root); }
  });
  new MutationObserver(records => {
    records.forEach(r => {
      if (r.type === 'attributes') watch(r.target);
      r.addedNodes?.forEach(n => n.nodeType === 1 && watch(n));
    });
  }).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'],
  });
  watch(document);

  // ---------------------------------------------------------------
  // クリック
  // ---------------------------------------------------------------
  document.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Pickup Products の開閉
    const toggle = target.closest('pickup-products-popup [data-popup-open], pickup-products-popup [data-popup-dismiss]');
    if (toggle) {
      send('pickup_popup_toggle', { action: toggle.hasAttribute('data-popup-dismiss') ? 'close' : 'open' });
      return;
    }

    const itemEl = WIDGETS.map(w => target.closest(w.item)).find(Boolean);
    if (!itemEl) return;
    // クイック追加は商品リンクの内側にあるため、先に判定する。
    // 色見本などほかのボタンは記録しない
    const isQuickAdd = !!target.closest('.card-quick-add, modal-opener');
    const isLink = !isQuickAdd && !target.closest('button') && !!target.closest('a[href*="/products/"]');
    if (!isQuickAdd && !isLink) return;

    const w = widgetOf(itemEl);
    const item = itemFor(w, itemEl);
    const touches = loadTouches();
    touches[item.item_id] = { list: item.item_list_id, page: location.pathname, at: Date.now() };
    saveTouches(touches);
    refreshCart();

    if (isQuickAdd) {
      send('widget_quick_add_click', { widget: item.item_list_id, item_id: item.item_id });
    } else {
      send('select_item', { item_list_id: item.item_list_id, item_list_name: w.name, items: [item] });
    }
  }, true);

  // ---------------------------------------------------------------
  // カート追加：ウィジェットで触った商品が入ったら記録する
  //   同じページで入った → via=quick_add（クイック追加）
  //   別のページで入った → via=product_page（リンクから商品ページへ移って追加）
  // ---------------------------------------------------------------
  const cartRequest = (input, init) => {
    const url = String(typeof input === 'string' ? input : input && input.url);
    const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST') return null;
    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) return 'add';
    if (/\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url)) return 'change';
    return null;
  };

  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    const kind = cartRequest(input, init);
    if (!kind) return promise;
    const before = cartValue;
    promise.then(res => {
      if (!res.ok) return;
      if (kind === 'change') { refreshCart(); return; }
      res.clone().json().then(body => {
        const added = (body.items || [body]).map(i => i && i.handle).filter(Boolean);
        const touches = loadTouches();
        const handle = added.find(h => touches[h]);
        refreshCart().then(after => {
          if (!handle) return;
          const t = touches[handle];
          delete touches[handle];
          saveTouches(touches);
          send('widget_add_to_cart', {
            widget: t.list,
            item_id: handle,
            via: t.page === location.pathname ? 'quick_add' : 'product_page',
            cart_value_before: before,
            cart_value_after: after,
            reached_free_shipping: before !== null && after !== null
              && before < FREE_SHIPPING_THRESHOLD && after >= FREE_SHIPPING_THRESHOLD ? 'yes' : 'no',
          });
        });
      }).catch(() => {});
    }).catch(() => {});
    return promise;
  };

  refreshCart();
})();
